<template>
  <div class="ingredients-list">
    <div class="ingredients-controls" v-if="hasPortions || hasImperial">
      <div v-if="hasImperial" class="unit-toggle">
        <span class="unit-toggle-label">Enheder</span>
        <div class="segmented" role="tablist">
          <button
            type="button"
            class="seg-btn"
            role="tab"
            :aria-selected="!useMetric"
            :class="{ active: !useMetric }"
            @click="useMetric = false"
          >Original</button>
          <button
            type="button"
            class="seg-btn"
            role="tab"
            :aria-selected="useMetric"
            :class="{ active: useMetric }"
            @click="useMetric = true"
          >Metrisk</button>
        </div>
      </div>
      <portions
        v-if="hasPortions"
        :value="portions"
        :original="originalPortions"
        :label="portionsLabel"
        :range-label="portionsRangeLabel"
        @input="portions = $event"
      />
    </div>

    <ul class="ingredient-rows">
      <template v-for="(row, index) in rows">
        <h6
          v-if="row.kind === 'title'"
          :key="'t-' + index"
          v-html="row.text"
        ></h6>
        <li
          v-else
          :key="'i-' + index"
          class="ingredient"
          :class="{ checked: checked.includes(index) }"
          @click="toggleChecked(index)"
          v-html="renderItem(row.text)"
        ></li>
      </template>
    </ul>
  </div>
</template>

<script>
import portions from '@/components/pages/recipe/Portions'
import unitHelper from '@/helpers/unitHelper'

const PORTION_WORDS = ['serveringer', 'portioner', 'person', 'personer']
const PORTION_REGEX = new RegExp(`\\b(${PORTION_WORDS.join('|')})\\b`, 'i')

export default {
  name: 'Ingredients',
  components: { portions },
  props: {
    ingredients: { type: Array, required: true }
  },
  data() {
    return {
      rows: [],
      portions: 0,
      originalPortions: 0,
      portionsLabel: 'Portioner',
      portionsRangeLabel: '', // populated when the recipe states a range (e.g. "4-6")
      useMetric: false,
      checked: []
    }
  },
  computed: {
    scale() {
      if (!this.originalPortions) return 1
      if (this.portionsRangeLabel) return 1 // range recipes are not scalable
      return this.portions / this.originalPortions
    },
    hasPortions() {
      return this.originalPortions > 0
    },
    hasImperial() {
      return this.rows.some(r => r.kind === 'item' && unitHelper.hasImperialUnit(r.text))
    }
  },
  methods: {
    isTitleLine(text) {
      const stripped = text.replace(/<[^>]*>/g, '').trim()
      if (!stripped) return false
      if (stripped.endsWith(':')) return true
      if (/<b>|<strong>/i.test(text)) return true
      return false
    },
    isPortionLine(text) {
      return PORTION_REGEX.test(text)
    },
    parsePortionLine(text) {
      const stripped = text.replace(/<[^>]*>/g, '')
      const labelMatch = stripped.match(PORTION_REGEX)
      const label = labelMatch
        ? labelMatch[1].charAt(0).toUpperCase() + labelMatch[1].slice(1).toLowerCase()
        : 'Portioner'

      // Detect a range: "4-6", "4 - 6", "4 til 6", "4 to 6". We use the lower
      // bound as the baseline but flag rangeLabel so the stepper goes inert.
      const rangeMatch = stripped.match(/(\d+)\s*(?:-|–|—|til|to)\s*(\d+)/i)
      if (rangeMatch) {
        const low = parseInt(rangeMatch[1], 10)
        const high = parseInt(rangeMatch[2], 10)
        if (low > 0 && high > low) {
          return { count: low, label, rangeLabel: `${low}–${high}` }
        }
      }

      const numMatch = stripped.match(/[\d½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/)
      if (!numMatch) return { count: 0, label, rangeLabel: '' }
      const fromIdx = stripped.indexOf(numMatch[0])
      const qty = unitHelper.parseQuantity(stripped.slice(fromIdx))
      const count = qty ? Math.max(1, Math.round(qty.value)) : 0
      return { count, label, rangeLabel: '' }
    },
    renderItem(text) {
      const parts = unitHelper.transformLine(text, {
        scale: this.scale,
        metric: this.useMetric
      })
      if (!parts) return text
      // Inputs are sanitized by the WYSIWYG editor (b/i/strong/em/br only),
      // and number/unit are generated locally - safe to concatenate.
      return (
        parts.prefix
        + '<span class="ingredient-number">' + parts.number + '</span>'
        + (parts.unit ? ' ' + parts.unit : '')
        + (parts.rest ? ' ' + parts.rest : '')
      )
    },
    toggleChecked(index) {
      const at = this.checked.indexOf(index)
      if (at === -1) this.checked.push(index)
      else this.checked.splice(at, 1)
    },
    parseIngredients(list) {
      const rows = []
      let portionsFound = false

      for (const raw of list) {
        if (!raw || !raw.trim()) continue
        if (!portionsFound && this.isPortionLine(raw)) {
          const { count, label, rangeLabel } = this.parsePortionLine(raw)
          if (count > 0) {
            this.originalPortions = count
            this.portions = count
            this.portionsLabel = label
            this.portionsRangeLabel = rangeLabel || ''
            portionsFound = true
            continue // hide the portion line from the rendered list
          }
        }
        rows.push({
          kind: this.isTitleLine(raw) ? 'title' : 'item',
          text: raw
        })
      }
      this.rows = rows
    }
  },
  created() {
    this.parseIngredients(this.ingredients || [])
  },
  watch: {
    ingredients(list) {
      this.checked = []
      this.originalPortions = 0
      this.portions = 0
      this.portionsRangeLabel = ''
      this.parseIngredients(list || [])
    }
  }
}
</script>

<style lang="scss">
.ingredients-list {
  background: white;
  border-radius: 10px;
  box-shadow: var(--cardshadow);
  overflow: hidden;

  .ingredients-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    background: linear-gradient(135deg, #f4f9ee 0%, #fafdf7 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 10px 10px 0 0;
  }

  .unit-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  .unit-toggle-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--dark);
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .segmented {
    display: inline-flex;
    background: white;
    border-radius: 999px;
    padding: 2px;
    box-shadow: var(--cardshadow);
    user-select: none;
  }

  .seg-btn {
    border: none;
    background: transparent;
    color: var(--dark);
    font-family: 'Poppins';
    font-size: 0.74rem;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: 999px;
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: background 0.18s ease, color 0.18s ease;
    line-height: 1;

    &:hover:not(.active) { color: var(--darker); }
    &.active {
      background: var(--darker);
      color: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }
    &:focus-visible {
      outline: 2px solid var(--darker);
      outline-offset: 2px;
    }
  }

  .ingredient-rows {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .ingredient-number {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  h6 {
    text-align: center;
    padding: 18px 20px 10px;
    text-transform: capitalize;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    margin: 0;
    font-size: 0.95rem;
    color: var(--dark);

    b, strong { font-weight: 700; }
    i, em { font-style: italic; }
  }

  li.ingredient {
    list-style: none;
    cursor: pointer;
    font-size: 0.9rem;
    padding: 10px 20px 10px 30px;
    margin: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    text-indent: -10px;
    transition: background 0.12s ease, color 0.12s ease;

    b, strong { font-weight: 700; }
    i, em { font-style: italic; }

    &:last-child {
      border-bottom: none;
      border-radius: 0 0 10px 10px;
    }

    &::before {
      content: '\00BB';
      color: var(--main);
      padding-right: 10px;
    }

    &.checked {
      background: var(--main);
      color: white;
      text-decoration: line-through;
      text-decoration-color: rgba(255, 255, 255, 0.5);

      &::before { color: white; }
      .ingredient-number { color: white; }
    }
  }
}

@media (max-width: 550px) {
  .ingredients-list .ingredients-controls {
    flex-direction: column;
    align-items: stretch;

    .portion-stepper,
    .unit-toggle {
      justify-content: space-between;
      width: 100%;
    }
  }
}
</style>
